import { useState, useEffect } from 'react';
import {
  PhotoIcon,
  TrashIcon,
  StarIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { Species } from '../../api/species';
import { Photo, getPhotosBySpecies, setSpeciesAvatar, getPhotoUrlFromObject, deletePhoto, uploadSpeciesPhoto } from '../../api/photos';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface SpeciesPhotoGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  species: Species;
  onAvatarUpdate: () => void;
}

export default function SpeciesPhotoGallery({
  isOpen,
  onClose,
  species,
  onAvatarUpdate,
}: SpeciesPhotoGalleryProps) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isOpen && species) {
      loadPhotos();
    }
  }, [isOpen, species]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const speciesPhotos = await getPhotosBySpecies(species.id);
      const photosWithAvatar = speciesPhotos.map(photo => ({
        ...photo,
        isAvatar: photo.id === species.avatarPhotoId,
      }));
      setPhotos(photosWithAvatar);
    } catch (error) {
      console.error('Error loading photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    setSelectedFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('photo', selectedFile);
      formData.append('speciesId', species.id);
      if (caption) {
        formData.append('caption', caption);
      }

      // Use the proper API function
      const uploadedPhoto = await uploadSpeciesPhoto(formData);
      
      setPhotos([...photos, { ...uploadedPhoto, isAvatar: false }]);
      setSelectedFile(null);
      setCaption('');
      toast.success('Photo uploaded successfully');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSetAvatar = async (photoId: string) => {
    try {
      await setSpeciesAvatar(species.id, photoId);
      setPhotos(photos.map(p => ({ ...p, isAvatar: p.id === photoId })));
      onAvatarUpdate();
      toast.success('Avatar updated successfully');
    } catch (error) {
      console.error('Error setting avatar:', error);
      toast.error('Failed to set avatar');
    }
  };

  const handleDeletePhoto = async () => {
    if (!selectedPhoto) return;

    try {
      await deletePhoto(selectedPhoto.id);
      
      // Close modal and reset state first
      setShowDeleteModal(false);
      setSelectedPhoto(null);
      
      // If the deleted photo was the avatar, refresh species data
      if (selectedPhoto.isAvatar) {
        onAvatarUpdate();
      }
      
      // Reload photos from server to ensure sync
      await loadPhotos();
      
      toast.success('Photo deleted successfully');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo');
    }
  };

  const openDeleteModal = (photo: Photo) => {
    setSelectedPhoto(photo);
    setShowDeleteModal(true);
  };

  return (
    <>
      <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
        <BrandModalHeader
          title={`Photo Gallery - ${species.commonName}`}
          subtitle="Manage photos for this species and set avatar image"
          onClose={onClose}
        />
        <BrandModalBody>
          {/* Upload Section */}
          <div className="mb-6">
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${
                dragActive
                  ? 'border-cyan-500 bg-cyan-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <img
                      src={URL.createObjectURL(selectedFile)}
                      alt="Preview"
                      className="h-32 w-32 object-cover rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={caption}
                      onChange={(e) => setCaption(e.target.value)}
                      placeholder="Photo caption (optional)"
                      className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                    />
                    <div className="flex justify-center space-x-2">
                      <button
                        type="button"
                        onClick={handleUpload}
                        disabled={uploading}
                        className="inline-flex items-center btn btn-primary text-sm shadow-sm disabled:opacity-50"
                      >
                        {uploading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        ) : (
                          <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                        )}
                        {uploading ? 'Uploading...' : 'Upload Photo'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedFile(null)}
                        className="btn btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-slate-400" />
                  <div className="text-sm text-slate-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer rounded-md font-medium text-cyan-600 hover:text-cyan-500">
                      <span>Upload a photo</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept="image/*"
                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                      />
                    </label>
                    <span className="pl-1">or drag and drop</span>
                  </div>
                  <p className="text-xs text-slate-500">PNG, JPG, GIF up to 5MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Photo Grid */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-slate-900">
              Current Photos ({photos.length})
            </h4>
            
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-12">
                <PhotoIcon className="mx-auto h-12 w-12 text-slate-400" />
                <h3 className="mt-2 text-sm font-medium text-slate-900">No photos</h3>
                <p className="mt-1 text-sm text-slate-500">Get started by uploading your first photo.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative group">
                    <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200">
                      <img
                        src={getPhotoUrlFromObject(photo)}
                        alt={photo.caption || 'Species photo'}
                        className="h-full w-full object-cover object-center group-hover:opacity-75"
                      />
                    </div>
                    
                    {/* Avatar Badge */}
                    {photo.isAvatar && (
                      <div className="absolute top-2 left-2">
                        <div className="flex items-center space-x-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800">
                          <StarIconSolid className="h-3 w-3" />
                          <span>Avatar</span>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex space-x-1">
                        {!photo.isAvatar && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSetAvatar(photo.id);
                            }}
                            className="rounded-full bg-white p-1 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-yellow-600"
                            title="Set as avatar"
                          >
                            <StarIcon className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            openDeleteModal(photo);
                          }}
                          className="rounded-full bg-white p-1 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-red-600"
                          title="Delete photo"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Caption */}
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2 text-xs text-white">
                        {photo.caption}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </BrandModalBody>
        <BrandModalFooter>
          <button type="button" className="btn btn-secondary text-sm" onClick={onClose}>
            Close
          </button>
        </BrandModalFooter>
      </BrandModal>

      {/* Delete Confirmation Modal */}
      <BrandModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        maxWidthClass="max-w-lg"
        zIndexClass="z-[60]"
      >
        <BrandModalHeader title="Delete Photo" onClose={() => setShowDeleteModal(false)} />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete this photo? This action cannot be undone.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger text-sm"
            onMouseDown={(e) => {
              e.preventDefault();
              handleDeletePhoto();
            }}
            onClick={handleDeletePhoto}
          >
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </>
  );
}
